/**
 * Auto-redeem landing page — handles /redeem/:code URLs.
 *
 * Behaviour:
 *   • If the user is not signed in: bounces to /login with a `next=/redeem/:code`
 *     query so they come straight back after login.
 *   • If signed in: POST /invite-codes/redeem immediately, show success/failure
 *     summary, then offer "Continue to Personal" CTA.
 *
 * The auto-redeem is intentional — share links should one-shot the user into a
 * reward. Manual entry (Settings / Pricing) is the alternative for code-typers.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Sparkles, Loader2, AlertTriangle, Check, Crown, Gift } from "lucide-react";
import { Button } from "../components/ui/button";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBilling } from "../contexts/BillingContext";
import { useTasks } from "../contexts/TaskContext";

export default function RedeemPage() {
  const { code: rawCode } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refresh: refreshBilling } = useBilling();
  const { refresh: refreshTasks } = useTasks();
  const code = (rawCode || "").toUpperCase();

  const [state, setState] = useState("loading"); // loading | success | error | needs-login
  const [reward, setReward] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (user === null) {
      // Still resolving auth
      return;
    }
    if (!user) {
      // Bounce through login so we can auto-redeem after auth
      navigate(`/login?next=${encodeURIComponent(`/redeem/${code}`)}`, { replace: true });
      setState("needs-login");
      return;
    }
    if (!code) {
      setError("No code provided.");
      setState("error");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/invite-codes/redeem", { code });
        setReward(data.reward_summary || "Code redeemed!");
        setState("success");
        await Promise.all([refreshBilling?.(), refreshTasks?.()]);
      } catch (e) {
        const friendly = formatApiError(e?.response?.data?.detail);
        const status = e?.response?.status;
        if (status === 409) setError(friendly || "You've already redeemed this code.");
        else if (status === 410) setError(friendly || "This code is no longer valid.");
        else if (status === 404) setError(friendly || "That code doesn't exist.");
        else setError(friendly || "Couldn't redeem that code.");
        setState("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, code]);

  return (
    <div
      data-testid="redeem-page"
      className="min-h-[80vh] flex items-center justify-center px-4 py-12"
    >
      <div className="w-full max-w-md rounded-2xl border border-border bg-card overflow-hidden anim-fade-up">
        {/* Header band */}
        <div
          className="px-6 pt-6 pb-4 border-b border-border text-center"
          style={{
            background:
              "linear-gradient(135deg, hsla(38, 92%, 50%, 0.18) 0%, transparent 70%)",
          }}
        >
          <div
            className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3"
            style={{
              backgroundColor: "hsla(38, 92%, 50%, 0.22)",
              color: "hsl(38, 92%, 50%)",
            }}
          >
            {state === "loading" && <Loader2 size={26} className="animate-spin" />}
            {state === "success" && <Sparkles size={26} />}
            {state === "error" && <AlertTriangle size={26} />}
            {state === "needs-login" && <Gift size={26} />}
          </div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-amber-500 mb-1">
            Invite Code
          </div>
          <h1
            data-testid="redeem-page-title"
            className="font-display text-2xl font-black tracking-tight"
          >
            {state === "loading" && "Redeeming…"}
            {state === "success" && "You're in 🎉"}
            {state === "error" && "Couldn't redeem"}
            {state === "needs-login" && "Sign in to claim"}
          </h1>
          <div className="font-mono text-xs text-muted-foreground mt-1">{code}</div>
        </div>

        {/* Body */}
        <div className="px-6 py-6 space-y-4">
          {state === "loading" && (
            <p className="text-sm text-muted-foreground text-center">
              Validating your code and applying the reward…
            </p>
          )}

          {state === "success" && (
            <>
              <div
                data-testid="redeem-page-reward"
                className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm flex items-start gap-2"
              >
                <Check size={16} className="text-emerald-500 mt-0.5 shrink-0" />
                <span>{reward}</span>
              </div>
              <Button
                onClick={() => navigate("/personal")}
                data-testid="redeem-page-continue-btn"
                className="w-full bg-amber-500 hover:bg-amber-500/90 text-black font-bold"
              >
                <Crown size={14} className="mr-1.5" /> Continue
              </Button>
            </>
          )}

          {state === "error" && (
            <>
              <div
                data-testid="redeem-page-error"
                className="rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm"
              >
                {error}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigate("/personal")}
                  data-testid="redeem-page-back-btn"
                >
                  Back to app
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => navigate("/pricing")}
                  data-testid="redeem-page-pricing-btn"
                >
                  Choose a plan
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
